import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import dayjs from 'dayjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
// import { TooltipComponent } from '../../../../shared/components/ui/tooltip/tooltip.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { SlidePanelComponent } from '../../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../../shared/components/ui/dropdown/dropdown.component';
import {
  IconComponent,
  IconName,
} from '../../../../shared/icons/icon.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { ApiService } from '../../../../core/services/api.service';
import { AuthService } from '../../../../core/services/auth.service';
import { SidebarService } from '../../../../core/services/sidebar.service';
import { ToasterService } from '../../../../core/services/toaster.service';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';

interface MenuItem {
  id: string;
  name: string; // from API
  path: string; // from API
  productName?: string; // not coming from API now
  icon?: string;
  sortOrder: number;
  status: string;
  createdAt?: string;
  updatedAt?: string;

  // UI only
  isActive?: boolean;
  parentName?: string;
}

@Component({
  standalone: true,
  selector: 'app-page-routes',
  templateUrl: './page-routes.component.html',
  imports: [
    ButtonComponent,
    CommonModule,
    SlidePanelComponent,
    ReactiveFormsModule,
    InputComponent,
    FormsModule,
    AlertComponent,
    MatSlideToggleModule,
    DropdownComponent,
    IconComponent,
    IndividualToasterComponent,
    AuditLogPanelComponent,
    // TooltipComponent,
  ],
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({ height: '0px', opacity: '0', visibility: 'hidden' })
      ),
      state(
        'expanded',
        style({ height: '*', opacity: '1', visibility: 'visible' })
      ),
      transition(
        'expanded \u003c=\u003e collapsed',
        animate('300ms ease-in-out')
      ),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageRoutesComponent implements OnInit {
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  apiservice: ApiService = inject(ApiService);
  authservice: AuthService = inject(AuthService);
  sidebarService = inject(SidebarService);
  toaster = inject(ToasterService);
  panelOpen = signal(false);
  isLoading = signal(false);
  form!: FormGroup;
  formSubmitted = signal(false);
  errorMessage = signal<string>('');
  selectedProduct = signal<string | null>('');
  selectedStatus = signal<string | null>('ACTIVE');
  selectedOrder = signal<string | null>(null);
  selectedParent = signal<string | null>('');
  selectedModule = signal<string | null>('');
  editId = signal<string>('');
  isEdit = signal<boolean>(false);
  successMessage = signal<string>('');
  filteredStatsData = signal<any[]>([]);
  menuList = signal<MenuItem[]>([]);
  filteredMenuList = signal<MenuItem[]>([]);
  productsList = signal<DropdownOption[]>([]);
  productRoutes = signal<any[]>([]); // Changed to array to store all products

  parentModuleList = signal<DropdownOption[]>([]); // Options for parent route selection
  expandedRows = signal<Set<string>>(new Set());
  // parentMenuList = signal<DropdownOption[]>([]);
  private dialog = inject(MatDialog);
  tableActionOptions = [
    {
      label: 'Edit',
      value: 'edit',
      iconName: 'squarePen' as IconName,
      isShow: true,
    },

    {
      label: 'Delete',
      value: 'delete',
      iconName: 'trash' as IconName,
      isShow: true,
    },
  ];

  auditEntityTypes = [AuditEntityType.APP_ROUTE];

  expandedProducts = signal<Set<string>>(new Set());

  toggleProduct(productId: string) {
    // this.expandedProducts.update((prev) => {
    //   const newSet = new Set(prev);
    //   if (newSet.has(productId)) {
    //     newSet.delete(productId);
    //   } else {
    //     newSet.add(productId);
    //   }
    //   return newSet;
    // });

    const currentExpanded = this.expandedProducts();
    const nextExpanded = new Set<string>();

    // If the clicked row wasn't expanded, expand only that row (closes others)
    // If it was already expanded, nextExpanded remains empty (collapses it)
    if (!currentExpanded.has(productId)) {
      nextExpanded.add(productId);
    }

    this.expandedProducts.set(nextExpanded);
  }

  moduleList = computed(() => {
    return [
      ...this.parentModuleList(),
      {
        label: 'Add New Module',
        value: 'addNewModule',
        isAction: true,
        icon: 'plus',
      },
    ];
  });

  statusOptions: DropdownOption[] = [
    { value: '', label: 'All' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
  ];

  orderOptions: DropdownOption[] = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
  ];

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      parentId: [''],
      productId: ['', Validators.required],
      icon: [''],
      path: ['', Validators.required],
      componentName: [''],
      componentPath: [''],
      moduleName: [''],
      moduleId: [''],
      onlyPath: [false, Validators.required],
      isPublic: [false, Validators.required],
      order: [0, [Validators.min(0)]],
    });

    // effect(
    //   () => {
    //     const routes = this.productRoutes();
    //     // Initialize with all products expanded by default
    //     if (routes && routes.length > 0) {
    //       const productIds = routes.map((p) => p.product.id);
    //       this.expandedProducts.update((prev) => {
    //         const newSet = new Set(prev);
    //         productIds.forEach((id: string) => newSet.add(id));
    //         return newSet;
    //       });
    //     }
    //   },
    //   { allowSignalWrites: true }
    // );

    effect(
      () => {
        const routes = this.productRoutes();

        if (routes && routes.length > 0) {
          const firstProductId = routes[0]?.product?.id;
          if (!firstProductId) return;

          this.expandedProducts.update((prev) => {
            // ✅ Prevent re-running when already initialized
            if (prev.size > 0) return prev;

            return new Set([firstProductId]);
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  parentMenuList = computed(() => {
    const productId = this.selectedProduct();
    const products = this.productRoutes();
    const currentId = this.editId();

    let allRoutes: any[] = [];

    // Extract routes based on product selection
    if (!productId) {
      allRoutes = products.flatMap((productData: any) => productData.routes || []);
    } else {
      const matching = products.find(
        (item: any) => item.product?.id === productId
      );
      allRoutes = matching?.routes || [];
    }

    // Helper to recursively flatten routes and submenus
    const flatten = (routes: any[]): any[] => {
      let result: any[] = [];
      routes.forEach((r) => {
        result.push(r);
        if (r.subMenus && r.subMenus.length > 0) {
          result = [...result, ...flatten(r.subMenus)];
        }
      });
      return result;
    };

    const flatRoutes = flatten(allRoutes);

    return flatRoutes
      .filter((r: any) => r.id !== currentId)
      .filter((r: any) => r.componentName == null)
      .map((item: any) => ({
        label: item?.name,
        value: item?.id,
      }));
  });

  isInvalid(
    controlName: string,
    hardCodeFormSubmitted: boolean = false
  ): boolean {
    const control = this.form.get(controlName);

    let condition =
      control && control.invalid && (control.touched || control.dirty);

    if (hardCodeFormSubmitted) {
      condition = condition && this.formSubmitted();
    } else {
      condition = condition || this.formSubmitted();
    }

    return !!condition;
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.loadMenuList();
    this.loadProducts();
    this.loadModuleList();
  }

  loadMenuList(refresh: boolean = false): void {
    this.isLoading.set(true);

    const params: any = {
      parentId: null,
      productId: null,
      status: this.selectedStatus(),
      sortOrder: this.selectedOrder() || 'asc',
    };

    if (this.selectedStatus() && this.selectedStatus() !== '') {
      params.status = this.selectedStatus();
    }

    this.apiservice
      .get('app-routes/list-routes', params, {
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: true,
        cache: !refresh,
      })
      .subscribe({
        next: (res: any) => {
          // Handle different response structures
          // Response might be: res, res.data, or direct object
          let responseData = res;

          // If response has a data property, use that
          if (res?.data) {
            responseData = res.data;
          }

          // Ensure it's an array
          if (!Array.isArray(responseData)) {
            responseData = [responseData];
          }

          // Collect all routes recursively from all products for dropdown selection
          const flatten = (routes: any[]): any[] => {
            let result: any[] = [];
            routes.forEach((r) => {
              result.push(r);
              if (r.subMenus && r.subMenus.length > 0) {
                result = [...result, ...flatten(r.subMenus)];
              }
            });
            return result;
          };

          const allProductsRoutes = responseData.flatMap(
            (product: any) => product.routes || []
          );
          const flatRoutes = flatten(allProductsRoutes);

          const routeOptions = flatRoutes.map((item: any) => ({
            label: item?.name,
            value: item?.id,
          }));

          // Set the signals
          this.productRoutes.set(responseData);
          this.isLoading.set(false);
        },

        error: () => {
          this.isLoading.set(false);
          this.errorMessage.set('Failed to load menu list.');
        },
      });
  }

  get parentList() {
    return [...this.parentMenuList()];
  }

  loadModuleList(): void {
    this.isLoading.set(true);

    // const params = {
    //   parentId: null,
    //   status: 'ACTIVE',
    //   productId: null,
    //   sortOrder: 'desc',
    // };

    this.apiservice
      .get('app-routes/list-app-route-module', {
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: true,
        cache: false,
      })
      .subscribe({
        next: (res: any) => {
          // Handle different response structures
          // Response might be: res, res.data, or direct object
          let responseData = res;

          // Collect all routes from all products for dropdown selection
          const moduleOptions = responseData.map((item: any) => ({
            label: item?.name,
            value: item?.id,
          }));
         
          // Set the signals
          this.parentModuleList.set(moduleOptions);
          this.isLoading.set(false);
        },

        error: () => {
          this.isLoading.set(false);
          this.errorMessage.set('Failed to load menu list.');
        },
      });
  }

  // Table configuration for paginated table
  // tableHeaders: TableHeader[] = [
  //   { label: 'Name', key: 'name', align: 'text-left' },
  //   { label: 'Order', key: 'sortOrder ', align: 'text-left' },
  //   {
  //     label: 'Parent',
  //     key: 'parentName ',
  //     align: 'text-center',
  //   },
  //   { label: 'Path', key: 'path', align: 'text-left' },
  //   {
  //     label: 'Icon',
  //     key: 'icon ',
  //     align: 'text-left',
  //   },
  //   {
  //     label: 'Created',
  //     key: 'createdAt',
  //     align: 'text-left',
  //     dateFormat: 'monthYear',
  //   },
  //   {
  //     label: 'Modified',
  //     key: 'updatedAt',
  //     align: 'text-left',
  //     dateFormat: 'monthYear',
  //   },
  // ];

  selectedRowItem(item: any) {
    if (!item || !item.row) return;

    const row = item.row;

    if (item.action === 'edit') {
    } else if (row?.id) {
      this.openConfirmationDialog(row);
    }
  }

  openConfirmationDialog(row: any) {
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete this item?`,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.deleteRoute(row?.id);
        }
      });
  }

  openPanel() {
    this.panelOpen.set(true);
  }

  callApi(refresh: boolean = false) {
    this.loadMenuList(refresh);
    this.loadModuleList();
    this.authservice.authenticate(true).subscribe((res) => {
      if (res) {
        this.sidebarService.callSidebarMethod();
      }
    });
  }

  closePanel() {
    this.panelOpen.set(false);
    this.editId.set('');
    this.isEdit.set(false);
    this.formSubmitted.set(false);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.selectedModule.set('');
    this.form.reset({
      name: '',
      path: '',
      icon: '',
      order: 0,
      onlyPath: false,
      componentPath: '',
      componentName: '',
      isPublic: false,
      productId: '',
      parentId: '',
      moduleId: '',
      moduleName: '',
    });
  }

  submitForm() {
    this.formSubmitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const path = 'pages/';

    const payload: any = {
      // id: this.editId || undefined,
      path: this.form.value.path,
      name: this.form.value.name,
      productId: this.selectedProduct(),
      parentId: this.form.value.parentId,
      icon: this.form.value.icon,
      sortOrder: Number(this.form.value.order),
      status: 'ACTIVE',
      onlyPath: this.form.value.onlyPath,
      componentPath: this.form.value.componentPath || null,
      componentName: this.form.value.componentName || null,
      isPublic: this.form.value.isPublic,
      moduleName: this.form.value.moduleName,
    };

    if (payload.parentId == '' || payload.parentId == null) {
      delete payload.parentId;
    }

    if (payload.icon == '' || payload.icon == null) {
      payload.icon = null;
    }

    if (this.form.value.moduleId === 'addNewModule') {
      payload.moduleName = this.form.value.moduleName?.toLowerCase() || null;
    } else {
      const moduleId = this.parentModuleList().find(
        (item: any) => item.value === this.form.value.moduleId
      );

      payload.moduleName = moduleId?.label?.toLowerCase() || null;
    }

    if (this.isEdit()) {
      payload.id = this.editId();
    }

    if (this.form.value.componentName) {
      const basePath = this.form.value.isPublic ? 'public' : 'private';
      let relativePath = this.form.value.componentPath;

      // Regex to detect and strip existing prefix like 'pages/public/moduleName/' or 'pages/private/moduleName/'
      // It matches: pages/ + (public or private) + / + (any non-slash characters for module) + /
      const prefixRegex = /^pages\/(public|private)\/[^\/]+\//;

      if (relativePath && prefixRegex.test(relativePath)) {
        relativePath = relativePath.replace(prefixRegex, '');
      }

      payload.componentPath = `${path}${basePath}/${payload.moduleName}/${relativePath}`;
    } else {
      payload.componentPath = null;
      payload.componentName = null;
    }

    const specialCharsPattern = /[^a-zA-Z\-_]/;
    if (payload.moduleName && specialCharsPattern.test(payload.moduleName)) {
      this.errorMessage.set(
        'Module name can only contain letters, hyphens, and underscores.'
      );
      return;
    }

    // CREATE
    if (!this.isEdit()) {
      this.apiservice
        .post('/app-routes/create-route', payload, {
          showSuccessToaster: true,
          showLoader: true,
        })
        .subscribe({
          next: (res) => {
            setTimeout(() => {
              this.callApi(true);
            }, 1000);
            this.isEdit.set(false);
            this.editId.set('');
            this.selectedModule.set('');
            this.toaster.success('Menu Added Successfully', 'Success', 2000);
            this.auditLogPanel?.reloadLogs();
            this.closePanel();
          },
          error: (error: any) => {
            this.errorMessage.set(
              error?.error ||
                error?.message ||
                'An error occurred while submitting the data. Please try again.'
            );
            this.formSubmitted.set(false);
          },
        });
      return;
    }

    if (this.isEdit()) {
      // UPDATE
      this.apiservice
        .put('/app-routes/update-route', payload, {
          showSuccessToaster: true,
          showLoader: true,
        })
        .subscribe({
          next: () => {
            setTimeout(() => {
              this.callApi(true);
            }, 500);
            this.isEdit.set(false);
            this.selectedModule.set('');
            this.editId.set('');
            this.toaster.success('Menu Updated Successfully', 'Success', 2000);
            this.auditLogPanel?.reloadLogs();
            this.closePanel();
          },
          error: (error: any) => {
            this.errorMessage.set(
              error?.error ||
                error?.message ||
                'An error occurred while submitting the data. Please try again.'
            );
            this.formSubmitted.set(false);
          },
        });
      this.form.markAllAsTouched();
    }
  }

  clearForm() {
    this.closePanel();
  }

  toggleTimeMode(event: any) {}

  loadProducts(): void {
    this.apiservice
      .get(
        '/product/list-product',
        {},
        {
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: true,
          cache: true,
        }
      )
      .subscribe({
        next: (response: any) => {
          const filtered = response
            .filter((item: any) => item.active)
            .map((item: any) => ({
              value: item.id,
              label: item.name,
            }));

          this.productsList.set(filtered);
        },
        error: (err: any) => console.error('Error loading products', err),
      });
  }

  /** -------------------------------
   * HANDLE PRODUCT DROPDOWN CHANGE
   * ------------------------------ */

  onProductChange(productId: string) {
    this.selectedProduct.set(productId);
  }

  onStatusFilter(status: string) {
    this.selectedStatus.set(status);
    this.loadMenuList(true);
  }

  onOrderFilter(order: string) {
    this.selectedOrder.set(order);
    this.loadMenuList();
  }

  onParentChange(event: any) {
   

    this.selectedParent.set(event);

    if (event) {
      this.form.get('componentPath')?.setValidators([Validators.required]);
      this.form.get('componentName')?.setValidators([Validators.required]);
      this.form.get('moduleId')?.setValidators([Validators.required]);
      this.form.get('componentName')?.updateValueAndValidity();
      this.form.get('componentPath')?.updateValueAndValidity();
      this.form.get('moduleId')?.updateValueAndValidity();

      this.form.get('componentPath')?.markAsTouched();
      this.form.get('componentName')?.markAsTouched();
      this.form.get('moduleId')?.markAsTouched();
    } else {
      this.form.get('componentPath')?.clearValidators();
      this.form.get('componentName')?.clearValidators();
      this.form.get('moduleId')?.clearValidators();
      this.form.get('componentName')?.updateValueAndValidity();
      this.form.get('componentPath')?.updateValueAndValidity();
      this.form.get('moduleId')?.updateValueAndValidity();

      this.form.get('componentPath')?.markAsPristine();
      this.form.get('componentName')?.markAsPristine();
      this.form.get('moduleId')?.markAsPristine();
    }
  }

  onModuleChange(event: any) {
    this.selectedModule.set(event);

    if (event === 'addNewModule') {
      this.form.get('moduleName')?.setValidators([Validators.required]);
      this.form.get('componentPath')?.setValidators([Validators.required]);
      this.form.get('componentName')?.setValidators([Validators.required]);

      this.form.get('moduleName')?.markAsTouched();
      this.form.get('componentPath')?.markAsTouched();
      this.form.get('componentName')?.markAsTouched();
    } else if (event == null) {
      this.form.get('moduleName')?.clearValidators();
      this.form.get('componentPath')?.clearValidators();
      this.form.get('componentName')?.clearValidators();
    } else {
      this.form.get('componentPath')?.setValidators([Validators.required]);
      this.form.get('componentName')?.setValidators([Validators.required]);

      this.form.get('componentPath')?.markAsTouched();
      this.form.get('componentName')?.markAsTouched();
    }

    this.form.get('moduleName')?.updateValueAndValidity();
    this.form.get('componentPath')?.updateValueAndValidity();
    this.form.get('componentName')?.updateValueAndValidity();
  }

  deleteRoute(id: string) {
    this.apiservice
      .delete(`app-routes/delete-route/${id}`, {
        showSuccessToaster: true,
        showLoader: true,
      })
      .subscribe({
        next: () => {
          this.toaster.success('Route Deleted Successfully', 'Success', 2000);
          this.auditLogPanel?.reloadLogs();
          setTimeout(() => {
            this.loadMenuList(true);
            this.authservice.authenticate(true).subscribe((res) => {
              if (res) {
                this.sidebarService.callSidebarMethod();
              }
            });
          }, 500);
        },
        error: (err) => {
          console.error('Delete failed', err);
        },
      });
  }

  clearFilters() {
    this.selectedStatus.set(null);
    this.selectedOrder.set(null);

   

    // this.selectedStatus.set('ACTIVE');
    // this.selectedOrder.set('asc');
    this.loadMenuList(); // Reload initial menu list
  }

  // Toggle row expansion
  toggleRow(routeId: string): void {
    const currentExpanded = this.expandedRows();
    const nextExpanded = new Set<string>();

    // If the clicked row wasn't expanded, expand only that row (closes others)
    // If it was already expanded, nextExpanded remains empty (collapses it)
    if (!currentExpanded.has(routeId)) {
      nextExpanded.add(routeId);
    }

    this.expandedRows.set(nextExpanded);
  }

  //   toggleRow(routeId: string): void {
  //   const currentExpanded = new Set(this.expandedRows());
  //   if (currentExpanded.has(routeId)) {
  //     currentExpanded.delete(routeId);
  //   } else {
  //     currentExpanded.add(routeId);
  //   }
  //   this.expandedRows.set(currentExpanded);
  // }

  // Format date helper
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return dayjs(dateString).format('MM-DD-YYYY');
  }

  hasChild = signal(false);
  // Edit route handler
  editRoute(route: any, event: Event, hasSubMenu: boolean): void {
    this.hasChild.set(hasSubMenu);
    event.stopPropagation();
    
    this.selectedProduct.set(route.productId);
    this.selectedParent.set(route.parentId || '');
    const moduleName = route.module?.name || '';
    const moduleId = route.module?.id || '';
    let componentPath = '';
    if (route?.componentPath) {
      let path = `pages/${route.isPublic ? 'public' : 'private'}/${
        route.module?.name || ''
      }/`;
      let splitPath = route.componentPath.split(path);

      componentPath = splitPath.pop();
     
    }

    this.form.patchValue({
      id: route.id,
      name: route.name,
      path: route.path,
      icon: route.icon,
      order: route.sortOrder,
      onlyPath: route.onlyPath,
      componentPath: componentPath,
      componentName: route.componentName,
      isPublic: route.isPublic,
      productId: route.productId,
      parentId: route.parentId,
      // moduleName: moduleName,
      moduleId: moduleId,
    });
    this.selectedModule.set(moduleId);
    this.isEdit.set(true);
    this.editId.set(route.id);
    this.openPanel();
    // TODO: Implement edit functionality
  }

  // Delete route confirmation
  deleteRouteConfirm(route: any, event: Event): void {
    event.stopPropagation();
    this.openConfirmationDialog(route);
  }

  isCheck = signal(false);

  toggleStatus(route: any): void {
    this.apiservice
      .put('/app-routes/update-status', { id: route.id, status: 'ACTIVE' })
      .subscribe({
        next: (res: any) => {
          this.loadMenuList(true);
          this.callApi();
          this.isLoading.set(false);
          this.toaster.success('Status updated successfully', 'Success', 2000);
        },
        error: (error: any) => {
          this.isLoading.set(false);
          this.toaster.error('Failed to update status', 'Error', 2000);
        },
      });

    // TODO: Implement toggle status functionality
  }
}
